/**
 * Local interview governance: redaction for exports / optional pre-send, retention helpers.
 */

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const PHONE_RE = /(?:\+?\d{1,3}[-.\s]?)?(?:\(?\d{2,4}\)?[-.\s]?)?\d{3}[-.\s]?\d{2,4}[-.\s]?\d{2,6}/g;

export function redactText(text, { redactEmail = false, redactPhone = false } = {}) {
  if (typeof text !== 'string' || !text) return text;
  let out = text;
  if (redactEmail) out = out.replace(EMAIL_RE, '[redacted-email]');
  if (redactPhone) out = out.replace(PHONE_RE, '[redacted-phone]');
  return out;
}

export function redactChatMessages(messages, opts) {
  if (!Array.isArray(messages)) return [];
  return messages.map((m) => {
    if (!m || typeof m !== 'object') return m;
    const content = typeof m.content === 'string' ? redactText(m.content, opts) : m.content;
    return { ...m, content };
  });
}

export function redactTranscriptTurns(turns, opts) {
  if (!Array.isArray(turns)) return [];
  return turns.map((t) => {
    if (!t || typeof t !== 'object') return t;
    const content = typeof t.content === 'string' ? redactText(t.content, opts) : t.content;
    return { ...t, content };
  });
}

/** Apply governance redaction to a shallow copy of flow input (chatHistory / interviewTranscript). */
export function redactFlowInputForSend(flowInput, governance) {
  if (!flowInput || typeof flowInput !== 'object' || !governance?.redactBeforeFlowSend) return flowInput;
  const opts = {
    redactEmail: Boolean(governance.redactEmailInExports ?? governance.redactEmail),
    redactPhone: Boolean(governance.redactPhoneInExports ?? governance.redactPhone),
  };
  if (!opts.redactEmail && !opts.redactPhone) return flowInput;
  const next = { ...flowInput };
  if (Array.isArray(next.chatHistory)) next.chatHistory = redactChatMessages(next.chatHistory, opts);
  if (Array.isArray(next.interviewTranscript)) {
    next.interviewTranscript = redactTranscriptTurns(next.interviewTranscript, opts);
  }
  return next;
}

export function buildInterviewExportPayload({
  chatHistory, transcript, interviewPhaseId, interviewSession, runtimeAuditEvents, governance,
}) {
  const opts = {
    redactEmail: Boolean(governance?.redactEmailInExports ?? governance?.redactEmail),
    redactPhone: Boolean(governance?.redactPhoneInExports ?? governance?.redactPhone),
  };
  return {
    exportedAt: new Date().toISOString(),
    interviewPhaseId: interviewPhaseId ?? null,
    interviewSession: interviewSession && typeof interviewSession === 'object' ? { ...interviewSession } : null,
    chatHistory: redactChatMessages(Array.isArray(chatHistory) ? chatHistory : [], opts),
    interviewTranscript: redactTranscriptTurns(Array.isArray(transcript) ? transcript : [], opts),
    runtimeAuditEvents: Array.isArray(runtimeAuditEvents) ? runtimeAuditEvents : [],
  };
}

export function downloadJsonInBrowser(filename, dataObj) {
  const blob = new Blob([JSON.stringify(dataObj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.replace(/[^\w.-]+/g, '_') || 'export.json';
  a.click();
  URL.revokeObjectURL(url);
}

export function interviewActivityStorageKey(cfg) {
  const appId = cfg?.id || cfg?._id || 'anonymous';
  const suffix = String(cfg?.metadata?.interviewFlow?.sharedChatHistoryStorageKey || 'default').slice(0, 64);
  return `interviewFlow:lastActivity:${appId}:${suffix}`;
}

/** @returns {number|null} retention in ms, or null if disabled */
export function getLocalInterviewRetentionMs(governance) {
  const d = governance?.localInterviewRetentionDays;
  if (d == null || d === '' || Number(d) <= 0) return null;
  const days = Math.min(3650, Math.max(1, Number(d)));
  return days * 24 * 60 * 60 * 1000;
}
